/*
 * Loose Ends — campaign analytics (spec §10).
 *
 * Fires the five defined events: page_view, form_submit, account_activation, gallery_open,
 * card_detail_open.
 *
 * DESIGN: THIS MODULE SUBSCRIBES, IT DOES NOT INTRUDE.
 * The campaign already broadcasts its own lifecycle as DOM events — loose-ends:joined,
 * loose-ends:enter-club, loose-ends:welcome-closed, loose-ends:detail-open — so everything here
 * is a listener. Four of the five events needed no change to the form or gallery code at all.
 * That is the point: instrumentation must not be able to break the campaign. If this file throws
 * on load, the join form and the wall carry on working.
 *
 * TRANSPORT: matches the house pattern (from the former snippets/_pdp-bot-checkout-tracker.liquid,
 * removed with Voiceflow in NOYZ-438) —
 * window.dataLayer for GTM (GTM-WMNQZRXX, loaded in layout/theme.liquid), then TripleWhale via
 * window.TriplePixel. Each in its own try/catch so one missing vendor can't take out the other.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 * NO EMAIL, EVER. NOT NEGOTIABLE.
 *
 * The `loose-ends:joined` event carries the visitor's email address in its detail payload,
 * because the form needs it to tell them which address they joined with. It must NOT go any
 * further. Pushing an email into dataLayer sends PII to GA4 — which prohibits it, and can get a
 * property suspended — and on to TripleWhale. form_submit reports whether they were already a
 * member, which theme they picked and whether they went anonymous. Never who they are.
 *
 * §10 does not mention this. It is added deliberately. Do not "complete" the payload.
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 */
(function () {
  'use strict';

  // Every event name and its parameters, in one place (§10: "keep names/params in one config
  // object"). Change a name here and it changes everywhere it is sent.
  var EVENTS = {
    PAGE_VIEW: 'loose_ends_page_view',
    FORM_SUBMIT: 'loose_ends_form_submit',
    ACCOUNT_ACTIVATION: 'loose_ends_account_activation',
    GALLERY_OPEN: 'loose_ends_gallery_open',
    CARD_DETAIL_OPEN: 'loose_ends_card_detail_open',
    // Added with the swipeable feed. CARD_DETAIL_OPEN above is deliberately UNCHANGED — it still
    // fires once, when a tile is tapped — so the dashboards already built on it keep their
    // meaning. These two describe what happens after that, which it cannot.
    FEED_POST_VIEW: 'loose_ends_feed_post_view',
    FEED_SESSION: 'loose_ends_feed_session',
  };

  var CAMPAIGN = 'loose-ends';

  function track(event, params) {
    var payload = Object.assign({ event: event, campaign: CAMPAIGN }, params || {});

    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
    } catch (e) {
      /* GTM absent or blocked — the TriplePixel call below still gets its chance. */
    }

    try {
      if (typeof window.TriplePixel === 'function') {
        window.TriplePixel('custom', event, payload);
      }
    } catch (e) {
      /* TripleWhale absent or blocked. */
    }
  }

  // Exposed so the activation page (which is a different template, and not part of the campaign
  // layout) can fire its one event without loading a second copy of this config.
  window.NoyzLooseEndsAnalytics = { track: track, EVENTS: EVENTS };

  var root = document.querySelector('[data-le-analytics]');
  var pageType = (root && root.getAttribute('data-le-analytics-page')) || 'campaign';
  var isMember = root && root.getAttribute('data-le-analytics-member') === 'true';

  // --- account_activation ------------------------------------------------------------------
  // ⚠️ APPROXIMATE, AND KNOWN TO BE. On the activation page the customer has no session yet —
  // they are in the middle of setting a password — so Liquid's `customer` is nil and there is no
  // way to tell whether an activation belongs to a Loose Ends joiner. This fires for EVERY
  // classic account activation, unattributed. During the campaign window nearly all of them will
  // be joiners, so the number is useful, but it is a proxy and not a measurement. Do not build a
  // precise funnel on it without saying so.
  //
  // Deliberately BEFORE page_view and returning early: the activation template is not a campaign
  // page and carries traffic that has nothing to do with Loose Ends, so counting it as a campaign
  // page view would inflate the top of the funnel with the very people who are already at the
  // bottom of it.
  if (pageType === 'activate_account') {
    track(EVENTS.ACCOUNT_ACTIVATION, {});
    return; // nothing else on this page to fire or listen for
  }

  // --- page_view -------------------------------------------------------------------------
  // GTM fires its own page views; this one is campaign-scoped and carries the campaign
  // parameters, so the funnel can be built without depending on URL matching.
  track(EVENTS.PAGE_VIEW, { page_type: pageType, is_member: isMember });

  // --- form_submit -------------------------------------------------------------------------
  document.addEventListener('loose-ends:joined', function (event) {
    var d = (event && event.detail) || {};
    // d.email is deliberately dropped here. See the header.
    track(EVENTS.FORM_SUBMIT, {
      already_member: !!d.alreadyMember,
      gallery_size: (d.gallery && d.gallery.length) || 0,
    });
  });

  // --- gallery_open ------------------------------------------------------------------------
  // Listens for the wall's own announcement rather than for the two events that *usually*
  // trigger it. A signed-in member auto-opens the wall without firing either of those, so
  // listening for the triggers would miss every returning member. The gallery already
  // de-duplicates, so this fires at most once per page.
  document.addEventListener('loose-ends:gallery-open', function (event) {
    var d = (event && event.detail) || {};
    track(EVENTS.GALLERY_OPEN, {
      trigger: d.trigger || 'unknown',
      tile_count: d.tile_count || 0,
      is_member: isMember,
    });
  });

  // --- card_detail_open --------------------------------------------------------------------
  document.addEventListener('loose-ends:detail-open', function (event) {
    var d = (event && event.detail) || {};
    track(EVENTS.CARD_DETAIL_OPEN, {
      post_id: d.id || null,
      theme: d.theme || null,
      anonymous: !!d.anon,
    });
  });

  // --- feed_post_view ------------------------------------------------------------------------
  // Fired by the gallery only after a card has SETTLED and stayed for a second, and only once per
  // post per feed session. Both gates live in the gallery, next to the state they need; this file
  // stays a pure listener.
  //
  // ⚠️ The gallery caps these at 40 per session. Past that the feed keeps working and the session
  // summary below still records the true depth, but the per-post rows stop. Read a session with
  // posts_viewed == 40 as "at least 40".
  document.addEventListener('loose-ends:feed-post', function (event) {
    var d = (event && event.detail) || {};
    track(EVENTS.FEED_POST_VIEW, {
      post_id: d.id || null,
      theme: d.theme || null,
      anonymous: !!d.anon,
      position: typeof d.position === 'number' ? d.position : null,
    });
  });

  // --- feed_session --------------------------------------------------------------------------
  // One row per session. This is the event that answers "did the feed increase engagement?" —
  // no per-card event can, because per-card rows cannot distinguish one person going deep from
  // many people bouncing.
  document.addEventListener('loose-ends:feed-session', function (event) {
    var d = (event && event.detail) || {};
    track(EVENTS.FEED_SESSION, {
      entry_post_id: d.entry_id || null,
      posts_viewed: typeof d.posts_viewed === 'number' ? d.posts_viewed : null,
      max_depth: typeof d.max_depth === 'number' ? d.max_depth : null,
      duration_ms: typeof d.duration_ms === 'number' ? d.duration_ms : null,
      exit_reason: d.reason || null,
    });
  });
})();
